'use client';

import Link from '@eliadmin/core/src/components/link';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';

import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { ProductCardSkeleton } from '../product/product-card/skeleton';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '../ui/carousel-progress';
import { useState } from 'react';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

export function SelectQueryProducts() {
  const [activeTab, setActiveTab] = useState<string>('newest');

  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();

  const { t } = useTranslations();

  const productsQuery = buildProductsQuery({
    order: 'desc',
    orderBy: activeTab === 'newest' ? 'id' : 'views',
  });

  const { data: products = { data: [] }, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, me?.authToken ?? null, activeTab, productsQuery],
    queryFn: () =>
      fetchProducts({
        locale,
        authToken: me?.authToken,
        query: {
          results: 8,
          page: 1,
          ...productsQuery,
        },
      }),
    enabled: isInitialized,
  });

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn('custom-section', 'gap-8 py-8 pr-0 sm:py-12 sm:pr-4 lg:py-16 xl:py-[72px]')}
    >
      <div className={cn('custom-container', 'flex items-center justify-between')}>
        <div className="flex items-center justify-start gap-6 sm:gap-8">
          <span
            onClick={() => setActiveTab('newest')}
            className={cn('ea-select-query-products__button', 'cursor-pointer text-[#9ca3af]', {
              'ea-select-query-products__button--active': activeTab === 'newest',
            })}
          >
            {t('text179')}
          </span>
          <span
            onClick={() => setActiveTab('most_viewed')}
            className={cn('ea-select-query-products__button', 'cursor-pointer text-[#9ca3af]', {
              'ea-select-query-products__button--active': activeTab === 'most_viewed',
            })}
          >
            {t('text181')}
          </span>
        </div>
        <Button asChild variant={'outline'} className="hidden sm:flex">
          <Link prefetch={false} href={ROUTES.PRODUCTS()}>
            {t('text494')} <HiOutlineArrowRightCircle size={24} />
          </Link>
        </Button>
      </div>
      <div className={cn('custom-container', 'h-[1px] bg-[#E5E7EB]')} />
      <div className={cn('custom-container', 'hidden grid-cols-4 gap-6 xl:grid')}>
        {isLoading
          ? [...Array(4)].map((_, index) => <ProductCardSkeleton key={index} />)
          : products.data.map(product => <ProductCard product={product} key={product.slug} />)}
      </div>
      <div className={cn('custom-container', 'relative block xl:hidden')}>
        <div className="absolute -left-[1000px] top-0 z-[30] h-full w-[1000px] cursor-context-menu bg-white" />
        <Carousel
          options={options}
          className={{
            main: 'w-full overflow-visible',
            container: 'w-full gap-4 overflow-visible',
            wrapper: 'w-full overflow-visible',
            slide: 'w-full max-w-[300px] md:max-w-[334px]',
          }}
          slides={
            isLoading
              ? [...Array(8)].map((_, index) => <ProductCardSkeleton key={index} />)
              : products.data.map(product => <ProductCard product={product} key={product.slug} />)
          }
        />
      </div>
      <div className="block w-full pr-4 sm:hidden">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={products.data.length} />
      </div>
      <div className="block w-full pr-4 sm:hidden">
        <Button asChild variant={'outline'} className="flex sm:hidden">
          <Link prefetch={false} href={ROUTES.PRODUCTS()}>
            {t('text494')} <HiOutlineArrowRightCircle size={20} />
          </Link>
        </Button>
      </div>
    </section>
  );
}
